// AvailableSubmissionsView.jsx - Display available submissions for head marker to claim
import React, { useState } from 'react';
import { Inbox, AlertTriangle, Trash, RotateCcw, ArrowLeftRight, FileText, PencilLine } from 'lucide-react';

function AvailableSubmissionsView({
  availableSubmissions,
  homeworkNames,
  onClaim,
  onForceDelete,
  onReset,
  onReopen,
  onChangeType
}) {
  const [filterValue, setFilterValue] = useState('all');
  const uniqueTypes = [...new Set(availableSubmissions.map(s => s.homeworkType))];
  const filtered = filterValue === 'all' ? availableSubmissions : availableSubmissions.filter(s => s.homeworkType === filterValue);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      <h2 style={{ margin: '0 0 16px 0', fontSize: '20px' }}>
        Available Submissions to Claim
      </h2>
      <p style={{ color: '#7f8c8d', marginBottom: '16px', fontSize: '14px' }}>
        These submissions are waiting to be marked. Claim one to start marking it.
      </p>

      {uniqueTypes.length > 1 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <button
            onClick={() => setFilterValue('all')}
            style={{
              padding: '8px 16px',
              background: filterValue === 'all' ? '#c33329' : '#f8f9fa',
              color: filterValue === 'all' ? 'white' : '#2c3e50',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px'
            }}
          >
            All ({availableSubmissions.length})
          </button>
          {uniqueTypes.map(type => (
            <button
              key={type}
              onClick={() => setFilterValue(type)}
              style={{
                padding: '8px 16px',
                background: filterValue === type ? '#c33329' : '#f8f9fa',
                color: filterValue === type ? 'white' : '#2c3e50',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px'
              }}
            >
              {homeworkNames[type] || type} ({availableSubmissions.filter(s => s.homeworkType === type).length})
            </button>
          ))}
        </div>
      )}

      {availableSubmissions.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px', 
          color: '#7f8c8d' 
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
            <Inbox size={48} color="#bdc3c7" />
          </div>
          <div style={{ fontSize: '18px' }}>No available submissions</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {filtered.map(submission => (
            <div
              key={submission.id}
              style={{
                padding: '16px',
                border: submission.isLate ? '2px solid #e74c3c' : '2px solid #e2e8f0',
                borderRadius: '8px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr auto',
                gap: '16px',
                alignItems: 'center',
                background: submission.isLate ? '#fff5f5' : 'white'
              }}
            >
              <div>
                <div style={{ fontWeight: '700', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {homeworkNames[submission.homeworkType] || submission.homeworkType}
                  {submission.isLate && (
                    <span style={{
                      background: '#e74c3c',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <AlertTriangle size={12} />
                      LATE
                    </span>
                  )}
                  {submission.submittedVia === 'exam_pdf_fallback' && (
                    <span style={{
                      background: '#f1f5f9',
                      color: '#475569',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <FileText size={12} />
                      PDF UPLOAD (NO EXAM SIM)
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                  {submission.studentName}
                  {submission.cohortName && (
                    <span style={{ 
                      marginLeft: '8px',
                      color: '#667eea',
                      fontWeight: '600'
                    }}>
                      • {submission.cohortName}
                    </span>
                  )}
                </div>
              </div>
              
              <div>
                <div style={{ fontSize: '13px', color: '#4a5568' }}>
                  Submitted: {formatDate(submission.submittedAt)}
                </div>
                {submission.isLate && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#e74c3c',
                    fontWeight: '600',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <AlertTriangle size={12} />
                    {Math.floor(submission.minutesLate / 60)}h {submission.minutesLate % 60}m late
                  </div>
                )}
                {submission.fileCount > 1 && (
                  <div style={{ fontSize: '12px', color: '#c33329', marginTop: '4px' }}>
                    {submission.fileCount} files
                  </div>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => onClaim(submission)}
                  style={{
                    padding: '10px 20px',
                    background: '#1b998b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Claim
                </button>
                {onReopen && submission.submittedVia === 'exam' && (
                  <button
                    onClick={() => onReopen(submission)}
                    title="Reopen for the student to keep editing - everything they typed stays, nothing is deleted (untimed homework typed in the app only)"
                    style={{
                      padding: '10px',
                      background: 'white',
                      color: '#2b6cb0',
                      border: '2px solid #2b6cb0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <PencilLine size={16} />
                  </button>
                )}
                {onReset && (
                  <button
                    onClick={() => onReset(submission)}
                    title="Reset this submission so the student can re-upload (use for broken/corrupted files)"
                    style={{
                      padding: '10px',
                      background: 'white',
                      color: '#f39c12',
                      border: '2px solid #f39c12',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <RotateCcw size={16} />
                  </button>
                )}
                {onChangeType && (
                  <button
                    onClick={() => onChangeType(submission)}
                    title="Change which homework type this is filed under (use when the right file was uploaded to the wrong slot) - the student is not notified"
                    style={{
                      padding: '10px',
                      background: 'white',
                      color: '#667eea',
                      border: '2px solid #667eea',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <ArrowLeftRight size={16} />
                  </button>
                )}
                {onForceDelete && (
                  <button
                    onClick={() => onForceDelete(submission)}
                    title="Permanently delete this submission (use for broken/corrupted submissions)"
                    style={{
                      padding: '10px',
                      background: 'white',
                      color: '#e74c3c',
                      border: '2px solid #e74c3c',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Trash size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AvailableSubmissionsView;